import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Twitter, Facebook, Linkedin, Youtube, Instagram,
  MapPin, Phone, Mail, Heart, ArrowRight, Send,
  ChevronRight, FlaskConical, GraduationCap, Lightbulb, Handshake
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';

const LINKS_1 = [
  ['About SAAI', '/about'],
  ['Impact Dashboard', '/impact'],
  ['Data & Insights Portal', '/insights'],
  ['Research & Evaluation', '/programs'],
  ['Capacity Development', '/programs'],
  ['Social Innovation', '/programs'],
  ['Grants & Partnerships', '/grants'],
];
const LINKS_2 = [
  ['Support SAAI', '/donate'],
  ['Volunteer', '/volunteer'],
  ['Partner With Us', '/partner'],
  ['Publications & Blog', '/blog'],
  ['Gallery', '/gallery'],
  ['Contact Us', '/contact'],
];

export default function Footer() {
  const { s } = useSettings();
  const [email,   setEmail]   = useState('');
  const [msg,     setMsg]     = useState('');
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/newsletter/subscribe', { email });
      setMsg('success');
      setEmail('');
    } catch { setMsg('error'); }
    finally { setLoading(false); }
  };

  const SOCIAL = [
    { icon: Twitter,   href: s('social_twitter','#'),   label:'Twitter' },
    { icon: Facebook,  href: s('social_facebook','#'),  label:'Facebook' },
    { icon: Linkedin,  href: s('social_linkedin','#'),  label:'LinkedIn' },
    { icon: Youtube,   href: s('social_youtube','#'),   label:'YouTube' },
    { icon: Instagram, href: s('social_instagram','#'), label:'Instagram' },
  ];

  return (
    <footer className="bg-royal-950 text-white">

      {/* CTA strip */}
      <div className="bg-gold-gradient">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-bold text-royal-950 mb-1">Commission Research or Partner With Us</h3>
            <p className="text-royal-800/70 text-sm">International NGOs, Ministries, academic institutions, and grant bodies welcome.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/partner" className="inline-flex items-center gap-2 px-6 py-3 bg-royal-900 text-white font-semibold rounded-full hover:bg-royal-800 transition-colors shadow-royal">
              <Handshake size={16} /> Partner With Us
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-royal-900 text-royal-950 font-semibold rounded-full hover:bg-royal-900/10 transition-colors">
              Get in Touch <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img src="/logo.jpeg" alt="SAAI" className="h-12 w-12 rounded-full object-cover ring-2 ring-gold-500/40" />
              <div>
                <div className="font-display font-bold text-lg">SAAI Institute</div>
                <div className="text-[10px] text-gold-400 uppercase tracking-widest leading-tight">
                  <span className="font-serif italic lowercase tracking-normal text-xs mr-1">of</span> Education & Capacity Development
                </div>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {s('footer_about', 'A pioneering data-driven research and capacity-building hub across West Africa, bridging AI, psychometrics, and educational innovation.')}
            </p>
            {/* Dept icons */}
            <div className="flex gap-2 mb-5">
              {[FlaskConical, GraduationCap, Lightbulb, Handshake].map((Icon, i) => (
                <div key={i} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-gold-500 hover:text-royal-950 flex items-center justify-center transition-all duration-200 text-gray-400">
                  <Icon size={14} />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              {SOCIAL.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label}
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-gold-500 hover:text-royal-950 flex items-center justify-center transition-all duration-200 text-gray-400">
                  <s.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-semibold text-xs uppercase tracking-widest text-gold-400 mb-5">Our Work</h4>
            <ul className="space-y-3">
              {LINKS_1.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors group">
                    <ChevronRight size={14} className="text-gold-500/50 group-hover:text-gold-500 transition-colors flex-shrink-0" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Engage */}
          <div>
            <h4 className="font-semibold text-xs uppercase tracking-widest text-gold-400 mb-5">Engage</h4>
            <ul className="space-y-3">
              {LINKS_2.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors group">
                    <ChevronRight size={14} className="text-gold-500/50 group-hover:text-gold-500 transition-colors flex-shrink-0" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10 space-y-2.5">
              <a href={`tel:${s('contact_phone','+234 800 000 0000')}`}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                <Phone size={14} className="text-gold-500" />{s('contact_phone','+234 800 000 0000')}
              </a>
              <a href={`mailto:${s('contact_email','info@saaingo.org')}`}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                <Mail size={14} className="text-gold-500" />{s('contact_email','info@saaingo.org')}
              </a>
              <div className="flex items-start gap-2 text-sm text-gray-400">
                <MapPin size={14} className="text-gold-500 mt-0.5 flex-shrink-0" />
                {s('contact_address','Abuja, FCT, Nigeria')}
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-xs uppercase tracking-widest text-gold-400 mb-5">Research Updates</h4>
            <p className="text-sm text-gray-400 mb-4 leading-relaxed">
              Subscribe for our latest publications, policy briefs, events, and program updates.
            </p>
            {msg === 'success' ? (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-sm text-emerald-400">
                🎉 Subscribed! Thank you for joining our network.
              </div>
            ) : (
              <form onSubmit={subscribe} className="space-y-2">
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com" required
                    className="w-full pl-9 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 transition-colors" />
                </div>
                {msg === 'error' && <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>}
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gold-500 hover:bg-gold-600 text-royal-950 font-semibold rounded-xl text-sm transition-colors disabled:opacity-60">
                  {loading ? 'Subscribing...' : <><Send size={14} />Subscribe</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} SAAI Institute of Education and Capacity Development. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {['Privacy Policy','Terms of Use','Sitemap'].map(l => (
              <a key={l} href="#" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </div>

      {/* WhatsApp float */}
      <a href={`https://wa.me/${s('whatsapp_number','2348000000000')}`} target="_blank" rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#20bf5a] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
        aria-label="WhatsApp">
        <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </footer>
  );
}